<script>
import { mapActions, mapState } from 'vuex';
import dateMixin from '../../mixins/dateMixin';
import currencies from '../../mixins/currencies';
import numberMixin from '../../mixins/numberMixin';
import link from '../../mixins/link';
import TriggerListsBookingMessages from '../../components/Triggers/TriggerListsBookingMessages';
import TriggerListsTableInfo from '../../components/Triggers/TriggerListsTableInfo';
import TriggerListsTableFilter from '../../components/Triggers/TriggerListsTableFilter';

export default {
  name: 'TriggerListsOverdueReminders',
  mixins: [
    dateMixin,
    currencies,
    numberMixin,
    link,
  ],
  components: {
    TriggerListsBookingMessages,
    TriggerListsTableInfo,
    TriggerListsTableFilter,
  },
  data() {
    return {
      headers: [
        { text: '', value: '', sortable: false, width: '35px', class: 'pa-0 pr-1' },
        { text: 'Booking ID', value: 'bookingId' },
        { text: 'Customer', value: 'customer' },
        { text: 'Supplier', value: 'bookingStay.supplier' },
        { text: 'Supplier Cancellation', value: 'bookingStay.latestDateToSupplierCancel' },
        { text: 'Domain', value: 'domainName' },
        { text: 'Booking Date', value: 'bookingStay.createdAt' },
        { text: 'Arrival Date', value: 'bookingStay.arrivalDate' },
        { text: 'Due Date', value: 'dueDate' },
        { text: 'Type', value: 'paymentRateType' },
        { text: 'Amount', value: 'amount' },
      ],
      pagination: {
        rowsPerPage: 25,
        sortBy: 'bookingStay.arrivalDate',
        descending: false,
      },
      showMessages: false,
    };
  },
  computed: {
    ...mapState({
      list: state => state.triggers.lists.list,
      filters: state => state.triggers.lists.filters,
    }),
    computedList() {
      if (!this.list.items.length) {
        return [];
      }
      return this.list.items.filter((booking) => {
        if (this.filters.chosenDomains.length && !this.filters.chosenDomains.includes(booking.domainId)) {
          return false;
        }
        if (this.filters.showMessages === 'only' && !booking.bookingStay.hasPaymentRemark) {
          return false;
        }
        if (this.filters.showMessages === 'none' && booking.bookingStay.hasPaymentRemark) {
          return false;
        }
        if (this.filters.showCancelled === 'only' && !booking.bookingStay.cancelledDate) {
          return false;
        }
        if (this.filters.showCancelled === 'none' && booking.bookingStay.cancelledDate) {
          return false;
        }
        if (this.filters.search) {
          const searchIn = `${booking.customer} ${booking.bookingStay.supplier}`.toLowerCase();
          return searchIn.indexOf(this.filters.search.trim().toLowerCase()) !== -1;
        }
        return true;
      });
    },
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      getList: 'triggers/getList',
      getMessages: 'triggers/getPaymentMessages',
    }),
    showPaymentMessages(booking) {
      this.getMessages(booking);
      this.showMessages = true;
    },
    getPaymentStatus(bookingStay) {
      if (bookingStay.paidAmount === 0) {
        return 'error';
      }
      if (bookingStay.paidAmount < bookingStay.totalAmount) {
        return 'orange';
      }
      if (bookingStay.paidAmount === bookingStay.totalAmount) {
        return 'green';
      }
      return 'blue';
    },
    goToBooking(booking) {
      const bookingLink = this.getLinkToTwentyPage(
        `booking/bookings/${booking.bookingId}/info?stay=${booking.bookingStay.id}`,
      );
      window.open(bookingLink, '_blank');
    },
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: 'Triggers', to: '', disabled: true },
        { text: 'Lists', to: '.' },
        { text: 'Overdue', to: this.$route.path },
      ],
      title: 'Lists',
    });
    this.getList('overdue-reminders');
  },
};
</script>
<template>
  <v-layout wrap>
    <v-flex xs12 mb-4>
      <h1>Overdue Bookings</h1>
      <span class="subheading">
        This list contains overdue bookings, that has not received a trigger
      </span>
    </v-flex>
    <trigger-lists-table-filter @refresh="getList('overdue-reminders')"/>
    <v-flex xs12 trigger-lists__overdue-table>
      <trigger-lists-table-info/>
      <v-data-table
        :headers="headers"
        :items="computedList"
        :loading="list.loading"
        :pagination.sync="pagination"
        must-sort
      >
        <template v-slot:items="props">
          <tr>
            <td class="pa-0 pr-2 trigger-lists__overdue-status">
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    :class="getPaymentStatus(props.item.bookingStay)"
                    class="trigger-lists__overdue-status-box"
                  />
                </template>
                <div>
                  <span>Paid: </span>
                  <span class="caption">{{ props.item.bookingStay.paidAmount | formatNumber }}</span>
                  <span class="py-2">-</span>
                  <span>Amount missing: </span>
                  <span class="caption">
                    {{ (props.item.bookingStay.totalAmount - props.item.bookingStay.paidAmount) | formatNumber }}
                  </span>
                </div>
              </v-tooltip>
              <v-tooltip :disabled="!props.item.bookingStay.cancelledDate" right>
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    :class="{ 'error': !!props.item.bookingStay.cancelledDate }"
                     class="trigger-lists__overdue-status-box green lighten-2"
                  />
                </template>
                <span>Cancelled: {{ formatDate(props.item.bookingStay.cancelledDate) }}</span>
              </v-tooltip>
              <v-tooltip v-if="props.item.bookingStay.hasPaymentRemark" right>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    class="teal--text text--lighten-3 trigger-lists__overdue-status-remark"
                    @click="showPaymentMessages(props.item)"
                  >
                    info
                  </v-icon>
                </template>
                <span class="body-2">Click to see remarks</span>
              </v-tooltip>
            </td>
            <td>
              <span class="cursor-pointer external-link blue--text" @click="goToBooking(props.item)">
                {{ props.item.bookingId }}
              </span>
            </td>
            <td>{{ props.item.customer }}</td>
            <td>{{ props.item.bookingStay.supplier }}</td>
            <td>
              {{ formatDateIgnoreTimezone(props.item.bookingStay.latestDateToSupplierCancel, 'DD-MM-YYYY') }}
              {{ props.item.bookingStay.supplierCancelPercent }}%
              ({{ props.item.bookingStay.daysBeforeNextSupplierCancel }})
            </td>
            <td>{{ props.item.domainName }}</td>
            <td>{{ formatDate(props.item.bookingStay.createdAt) }}</td>
            <td>
              {{ formatDate(props.item.bookingStay.arrivalDate, 'DD-MM-YYYY') }}
              ({{ props.item.bookingStay.daysBeforeArrival }})
            </td>
            <td>{{ formatDateIgnoreTimezone(props.item.dueDate, 'DD-MM-YYYY') }}</td>
            <td>{{ props.item.paymentRateType }}</td>
            <td>{{ props.item.amount | formatNumber }} {{ formatCurrency(props.item.currency) }}</td>
          </tr>
        </template>
      </v-data-table>
      <trigger-lists-booking-messages :visible="showMessages" @hide="showMessages = false"/>
    </v-flex>
  </v-layout>
</template>

<style lang="scss">
.trigger-lists__overdue {
  &-table {
    position: relative;
  }

  &-status {
    font-size: 0 !important;
    position: relative;

    &-box {
      height: 100%;
      width: 12px;
      display: inline-block;
    }

    &-remark {
      position: absolute;
      top: 50%;
      right: -8px;
      transform: translateY(-50%);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/triggers/TriggerListsOverdueReminders.vue